import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { localize } from 'common/utils';
import { solidify } from 'modules/menu/solidify';
import { useOfferingMenuFeaturesContext } from 'modules/shared-menu/contexts/OfferingMenuFeaturesContext';
import { SelectionCategory } from 'modules/shared-menu/types';

import MenuEditorDnDItem from '../../dnd/MenuEditorDnDItem';
import { MenuEditorDragData } from '../../dnd/types';
import NavigationItem from '../NavigationItem';

type Props = {
  menuEditorRootUrl: string;
  language: string;
  category: SelectionCategory;
};

const CategoryNavItemDragOverlay = styled(NavigationItem)`
  background-color: ${cssThemedValue({
    light: solidify(colors.pepper8, 'light'),
    dark: solidify(colors.salt8, 'dark'),
  })} !important;
`;

const StyledMenuEditorDnDItem = styled(MenuEditorDnDItem)`
  margin: -8px;
  padding-block-start: 8px;
  padding-block-end: 8px;
`;

const StyledNavigationItem = styled(NavigationItem)<{ isDragging: boolean }>`
  opacity: ${({ isDragging }) => (isDragging ? '0.6' : '1')};
`;

const shrinkOverlayTypes: MenuEditorDragData['type'][] = [
  'DND_EDITOR_SINGLE_ITEM',
  'DND_EDITOR_MULTIPLE_ITEMS',
  'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM',
  'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS',
];

export const DndCategoryItem = ({ menuEditorRootUrl, language, category }: Props) => {
  const menuFeatures = useOfferingMenuFeaturesContext();
  const data: MenuEditorDragData = useMemo(
    () => ({
      type: 'DND_NAVIGATION_CATEGORY',
      category,
      dragOverlay: (
        <CategoryNavItemDragOverlay
          label={localize(category.name, language)}
          numberOfItems={category.offer_bindings?.length ?? 0}
        />
      ),
      shrinkOverlayTypes,
    }),
    [category, language],
  );
  return (
    <StyledMenuEditorDnDItem id={category.id} data={data}>
      {({ isDragging, active, isOver }) => {
        const activeType = active?.data.current?.type;
        const isDroppable = shrinkOverlayTypes.includes(activeType) && isOver;

        return (
          <StyledNavigationItem
            label={localize(category.name, language)}
            imageUrl={menuFeatures.categoryImages ? category.image : undefined}
            imageBlurhash={menuFeatures.categoryImages ? category.image_blurhash : undefined}
            isDragging={isDragging}
            numberOfItems={category.offer_bindings?.length ?? 0}
            href={`${menuEditorRootUrl}/${category.id}`}
            isDroppable={isDroppable}
          />
        );
      }}
    </StyledMenuEditorDnDItem>
  );
};
